/**
 * Validasi & deep-link RustDesk sisi klien (ADR-0054 §5). Penegakan sebenarnya milik server
 * (DTO `@Matches` + aturan satu-komputer-per-alat); modul ini hanya mencegah kiriman yang pasti
 * ditolak dan menyusun tautan ke komputer alat.
 */

export function normalizeRustdeskId(value: string): string {
  return value.replace(/\s+/g, '');
}

/** `null` = sah. Selain itu, kalimat yang bisa dibaca pengguna. */
export function rustdeskIdProblem(value: string): string | null {
  const v = normalizeRustdeskId(value);
  if (v.length === 0) {
    return 'RustDesk ID wajib diisi.';
  }
  if (!/^[0-9]{6,16}$/.test(v)) {
    return 'RustDesk ID harus 6–16 digit angka.';
  }
  return null;
}

export function namaAlatProblem(value: string): string | null {
  return value.trim().length === 0 ? 'Nama alat wajib diisi.' : null;
}

/** Deep-link ke SATU komputer milik alat — tak ada mesin lain (ADR-0054 §5). */
export function rustdeskDeepLink(rustdeskId: string): string {
  return `rustdesk://${encodeURIComponent(normalizeRustdeskId(rustdeskId))}`;
}

/**
 * Penyaringan alat per wilayah — ADR-0054 Fase 2, keputusan Owner "filter via wilayah di alat"
 * (opsi paling ringan, TANPA tabel pemetaan teknisi↔wilayah). Teknisi/manajer mempersempit
 * installed base ke provinsinya memakai field `provinsi` yang sudah ada di alat.
 *
 * MURNI, tanpa React, tanpa jaringan: menyaring daftar yang SUDAH diambil & sudah ter-scope
 * tenant di server. Ini kenyamanan tampilan, bukan kontrol akses — isolasi tenant & cakupan
 * peran tetap ditegakkan server saat mengambil daftarnya.
 */

/** Daftar provinsi unik yang benar-benar ada pada alat — di-trim, tanpa kosong, urut (id). */
export function provinsiTersedia(
  assets: readonly { readonly provinsi: string | null }[],
): string[] {
  const set = new Set<string>();
  for (const a of assets) {
    const p = a.provinsi?.trim();
    if (p && p.length > 0) set.add(p);
  }
  return Array.from(set).sort((x, y) => x.localeCompare(y, 'id'));
}

/**
 * Menyaring alat menurut provinsi. `provinsi` kosong = TANPA filter (tampilkan semua, termasuk
 * alat yang belum berprovinsi). Selain itu cocokkan pada provinsi alat yang sudah di-trim —
 * sehingga alat tanpa provinsi TIDAK muncul saat sebuah provinsi dipilih.
 */
export function saringAlatProvinsi<T extends { readonly provinsi: string | null }>(
  assets: readonly T[],
  provinsi: string,
): readonly T[] {
  const target = provinsi.trim();
  if (target.length === 0) return assets;
  return assets.filter((a) => (a.provinsi?.trim() ?? '') === target);
}
